import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Avatar,
  CircularProgress,
  Divider,
  Chip,
  Button
} from '@mui/material';
import {
  Warning as AlertIcon,
  ArrowForward as GoIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import indexedDBService from '../../utils/indexedDBService';

interface LowStockItem {
  id: string;
  name: string;
  quantity: number;
  minLevel: number;
}

const LowStockAlertsWidget: React.FC = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<LowStockItem[]>([]);

  useEffect(() => {
    if (currentUser) fetchData();
  }, [currentUser]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const allInventory = await indexedDBService.getAllItems<any>('inventory');
      const userInventory = allInventory.filter(i => i.userId === currentUser?.uid);

      // Detect low stock items
      // Default threshold if not set = 10 units
      const lowStock = userInventory
        .map(item => ({
          id: item.id,
          name: item.name || 'Unnamed Item',
          quantity: parseFloat(item.stockQuantity || item.quantity || 0),
          minLevel: parseFloat(item.minStockLevel || item.lowStockAlertLevel || 5)
        }))
        .filter(item => item.quantity <= item.minLevel)
        .sort((a, b) => a.quantity - b.quantity)
        .slice(0, 5);

      setItems(lowStock);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card sx={{ borderRadius: 3, height: '100%', border: items.length > 0 ? '1px solid #fee2e2' : 'none' }}>
      <CardHeader
        title={<Typography variant="h6" fontWeight="bold">Stock Alerts</Typography>}
        subheader={items.length > 0 ? `${items.length} items running critically low` : "All stock stable"}
        avatar={
          <Avatar sx={{ bgcolor: items.length > 0 ? 'error.main' : 'success.main' }}>
            <AlertIcon />
          </Avatar>
        }
        action={
          <Button component={Link} to="/inventory" size="small" endIcon={<GoIcon />} sx={{ fontWeight: 'bold' }}>
            View All
          </Button>
        }
      />
      <Divider />
      <CardContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
        ) : items.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" fontWeight="bold" color="success.main">✓ Everything is fully stocked.</Typography>
          </Box>
        ) : (
          <List disablePadding>
            {items.map((item, idx) => (
              <React.Fragment key={item.id}>
                <ListItem
                  secondaryAction={
                    <Chip
                      label={`${item.quantity} left`}
                      color={item.quantity <= 0 ? 'error' : 'warning'}
                      size="small"
                      sx={{ fontWeight: 'bold' }}
                    />
                  }
                  sx={{ px: 0 }}
                >
                  <ListItemText
                    primary={<Typography variant="subtitle2" fontWeight="bold">{item.name}</Typography>}
                    secondary={`Threshold: ${item.minLevel}`}
                  />
                </ListItem>
                {idx < items.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
};

export default LowStockAlertsWidget;
